import React, { useEffect, useReducer, useState } from "react";
import { Dialog, DialogActions, DialogTitle } from "@mui/material";
import { FileError } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { ApiError } from "@common/api";
import { useErrorNotification } from "@common/components/Notification";
import { uploadErrorMapper } from "@common/utils/translation";
import { uploadLogo } from "@services/logo";
import Button from "@common/components/Button";
import { uploadCatalog } from "@services/catalog";
import { addAttachment } from "@services/tickets";
import { postImportIssues, postImportRoomTypes } from "@services/customization";
import { useParams, useSearchParams } from "react-router-dom";
import { uploadRoomAttachment } from "@services/rooms";
import { postImportLicense } from "@services/license";
import UploadDialogContent from "./UploadDialogContent";
import { postBillingCode } from "@services/account";
import { postUsers } from "@services/user";
import { BackendConvertSheets, fileConvert } from "@services/devices";
import {
  IImportMode,
  ImportMode,
} from "@pages/Assets/common/ImportListContext";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { useAssetNavigationParams } from "@pages/Assets/common/const";

export enum UploadType {
  Logo,
  Catalog,
  Ticket,
  FieldIssue,
  RoomType,
  Room,
  Licenses,
  BillingCode,
  CSV_IMPORT,
  Users,
}

interface UploadDialogProps {
  open?: boolean;
  logoOnClose?: (path: string) => void;
  onClose?: () => void;
  onSuccess?: () => void;
  objectId?: string | number;
  logoClass?: string;
  uploadType: UploadType;
  refetch?: () => void;
  acceptedFiles?: { [key: string]: string[] };
  uploadFunction?: (file: File | null | undefined) => void;
  validator?: (file: File) => FileError | null;
  title: string;
  licenseAccountId?: string;
  additionalText?: string;
  setConvertedFile?: (
    sheets: BackendConvertSheets,
    importMode: IImportMode
  ) => void;
  onManual?: () => void;
  fileFormatInformation?: string;
}

export type DialogState = {
  selectedFile?: File | null;
  errorFile?: string;
  fileName?: string;
  fileSize?: string;
};

const UploadDialog = (props: UploadDialogProps) => {
  const {
    open = false,
    logoOnClose,
    onClose,
    onSuccess,
    objectId,
    logoClass,
    uploadType,
    refetch,
    uploadFunction,
    setConvertedFile = () => {},
    acceptedFiles = {},
    validator = () => {
      return null;
    },
    title,
    licenseAccountId,
    onManual = () => {},
    fileFormatInformation,
    additionalText,
  } = props;

  const [importMode, setImportMode] = useState<IImportMode>(ImportMode.EXCEL);
  const [accounts] = useUserSelectedAccounts();
  const { t } = useTranslation();
  const [{ selectedFile, errorFile, fileName, fileSize }, dispatch] =
    useReducer(
      (state: DialogState, changes: DialogState) => ({ ...state, ...changes }),
      {
        selectedFile: null,
        errorFile: "",
        fileName: "",
        fileSize: "",
      }
    );
  const [user] = useAuthenticatedUser();
  const [, , , , , , , , refreshAccounts] = useUserSelectedAccounts();

  const { accountid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get("accountId");
  const {
    fromAccount: fromAccount,
    fromRoom,
    fromLocation,
  } = useAssetNavigationParams().navigationParams;
  const { watchList: watchList } = useAssetNavigationParams().navigationParams;

  const [targetAccountId, setTargetAccountId] = useState<string | undefined>(
    accounts.length === 1 && watchList !== "true" ? accounts[0] : undefined
  );

  const [progress, setProgress] = useState<number>(0);
  const [showProgressBar, setShowProgressBar] = useState<boolean>(false);
  const mustHaveAccount =
    (uploadType == UploadType.CSV_IMPORT &&
      fromAccount === undefined &&
      accounts.length > 1) ||
    watchList === "true";

  const formatSearchParams = (
    targetAccountId: string,
    fromRoom?: string,
    fromLocation?: string
  ) => {
    const params: Record<string, string> = { fromAccount: targetAccountId };

    if (fromRoom) params.fromRoom = fromRoom;
    if (fromLocation) params.fromLocation = fromLocation;
    return `?${new URLSearchParams(params)}`;
  };

  const dispatchError = useErrorNotification();
  const onConfirm = async () => {
    dispatch({ errorFile: "" });
    if (selectedFile != null) {
      setProgress(0);
      setShowProgressBar(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      try {
        switch (uploadType) {
          case UploadType.Logo:
            let path = await uploadLogo(
              formData,
              objectId as string,
              logoClass ?? "",
              setProgress
            );
            logoOnClose && logoOnClose(path);
            break;
          case UploadType.Catalog:
            await uploadCatalog(formData, setProgress);
            break;
          case UploadType.Ticket:
            await addAttachment(
              formData,
              fileName ?? "",
              objectId as number,
              user?.email ?? "",
              setProgress
            );
            break;
          case UploadType.FieldIssue:
            await postImportIssues(
              accountid ?? "",
              formData,
              setProgress,
              objectId as string
            );
            break;
          case UploadType.RoomType:
            await postImportRoomTypes(
              accountid ?? licenseAccountId ?? "",
              formData,
              setProgress
            );
            break;
          case UploadType.Room:
            await uploadRoomAttachment(
              formData,
              `${objectId ?? ""}`,
              setProgress
            );
            break;
          case UploadType.CSV_IMPORT:
            let convertedFile = await fileConvert(formData, setProgress);
            setConvertedFile(convertedFile, importMode);
            if (targetAccountId)
              setSearchParams(
                formatSearchParams(targetAccountId, fromRoom, fromLocation),
                {
                  replace: true,
                }
              );
            break;
          case UploadType.Licenses:
            await postImportLicense(accountid ?? "", formData, setProgress);
            refreshAccounts();
            break;
          case UploadType.BillingCode:
            await postBillingCode(accountid ?? "", formData, setProgress);
            break;
          case UploadType.Users:
            await postUsers(accountId ?? "", formData, setProgress);
            break;
          default:
            break;
        }
        onSuccess && onSuccess();
      } catch (e) {
        const errorText = uploadErrorMapper(
          e as ApiError,
          t,
          (e as ApiError).response.data.errorContext?.entity ??
            (e as ApiError).response.data.errorContext?.invalidArgument,
          (e as ApiError).response.data.errorContext?.maxValue
        );
        dispatchError(errorText);
      } finally {
        dispatch({ selectedFile: null, fileName: "", fileSize: "" });
        setShowProgressBar(false);
        onClose && onClose();
        refetch && refetch();
      }
    } else if (importMode === ImportMode.MANUAL) {
      switch (uploadType) {
        case UploadType.CSV_IMPORT:
          onManual();
          if (targetAccountId)
            setSearchParams(
              formatSearchParams(targetAccountId, fromRoom, fromLocation),
              {
                replace: true,
              }
            );

          break;
        default:
          break;
      }
    }
  };

  const delayedUploadFile = () => {
    uploadFunction && uploadFunction(selectedFile);
    dispatch({ selectedFile: null, fileName: "", fileSize: "" });
    setShowProgressBar(false);
    onClose && onClose();
  };

  const onCancel = () => {
    dispatch({
      selectedFile: null,
      fileName: "",
      fileSize: "",
      errorFile: "",
    });
    setShowProgressBar(false);
    setTargetAccountId(undefined);
    onClose && onClose();
    logoOnClose && logoOnClose("");
  };

  useEffect(() => {
    if (open && importMode !== ImportMode.EXCEL)
      setImportMode(ImportMode.EXCEL);
  }, [open]);

  const displayDropzone = importMode !== ImportMode.MANUAL;

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={() => {
        onClose && onClose();
        logoOnClose && logoOnClose("");
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <UploadDialogContent
        additionalText={additionalText}
        acceptedFiles={acceptedFiles}
        uploadType={uploadType}
        validator={validator}
        showProgressBar={showProgressBar}
        progress={progress}
        errorFile={errorFile}
        fileName={fileName}
        fileSize={fileSize}
        dispatch={dispatch}
        fileFormatInformation={fileFormatInformation}
        importMode={importMode}
        setImportMode={setImportMode}
        targetAccountId={targetAccountId}
        setTargetAccountId={setTargetAccountId}
        mustHaveAccount={mustHaveAccount}
      />
      <DialogActions>
        <Button
          color="white"
          onClick={uploadFunction ? delayedUploadFile : onConfirm}
          disabled={
            (selectedFile === null && displayDropzone) ||
            (mustHaveAccount && targetAccountId === undefined) ||
            showProgressBar
          }
          data-testid={displayDropzone ? "upload-button" : "create-button"}
        >
          {displayDropzone ? <>{t("Upload")}</> : <>{t("Create")}</>}
        </Button>
        <Button
          variant="outlined"
          color="greyed"
          onClick={onCancel}
          data-testid="cancel-button"
        >
          {t("Cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
